import React from 'react';

import { graphql, PageProps } from 'gatsby';

import { BreadCrumbData } from '@mewa/ui';

import t from '../../libs/translate';
import { useConversionButtonLabel } from '../a-b-testing/cta-label-experiment';
import { NavigationData } from '../components/header/types';
import { GlobalPageContext } from '../types';
import CollectionPage, {
  Collection,
} from './components/collection-page/collection-page';
import { prepareBrochureSelection } from './conversion/brochures-download/helper';
import { storeServiceSegment } from './conversion/persistence';

export { Head } from '../layouts/head';

type Props = NavigationData & {
  pimCollection: Omit<Collection, 'infoText2'>;
  translations: {
    general_download_brochure: string;
    show_product_details_label: string;
    navigation_product_menu_label: string;
  };
};

type PageContext = GlobalPageContext;

export const query = graphql`
  query (
    $marketRegex: String!
    $id: String!
    $lang: String!
    $locale: String!
  ) {
    ...NavigationFragment

    pimCollection(id: { eq: $id }) {
      name
      claim
      property
      infoText1
      products {
        ...ProductData
      }
      assets {
        keyVisualDesktop
        keyVisualMobile
      }
    }

    translations: translation(locale: { eq: $locale }) {
      general_download_brochure
      show_product_details_label
      navigation_product_menu_label
    }
  }
`;

const ProductCollection = ({
  data: {
    pimCollection: collection,
    translations,
    navigationLinks: { conversion, navigation },
    navigationClothing: { nodes: serviceSegments },
    headerAndFooterTranslations,
  },
  pageContext: { langPrefix, locale },
}: PageProps<Props, PageContext>) => {
  const { ctaLabel } = useConversionButtonLabel({
    translations: headerAndFooterTranslations,
  });

  const includedServiceSegments = Array.from(
    new Set(collection.products.map((p) => p.serviceSegment.key)),
  );

  const serviceSegment = serviceSegments.find(
    (serviceSegment) => serviceSegment.key === includedServiceSegments[0],
  );

  const breadCrumbData: BreadCrumbData[] = [
    {
      name: navigation.productMenu.name,
      slug: `${langPrefix}/${navigation.productMenu.slug}/`,
    },
    {
      name: navigation.clothing.name,
      slug: `${langPrefix}/${navigation.clothing.slug}/`,
    },
    {
      name: serviceSegment?.name || '',
      slug: `${langPrefix}/${serviceSegment?.slug}/`,
    },
    {
      name: collection.name,
      slug: '',
    },
  ];

  const mobileBreadCrumbData: BreadCrumbData = {
    name: navigation.clothing.name,
    slug: `${langPrefix}/${navigation.clothing.slug}/`,
  };

  return (
    <CollectionPage
      data-pagefind-meta={`collection_name:${collection.name}`}
      isClothingCategory={true} // All collections are MBS
      langPrefix={langPrefix}
      collection={{
        ...collection,
        infoText2: null,
      }}
      detailsPageLabel={t(translations, 'show_product_details_label')}
      conversion={{
        label: ctaLabel,
        link: `${langPrefix}/${conversion.contact.slug}`,
        onClick: () => {
          storeServiceSegment(includedServiceSegments[0]);
        },
      }}
      brochuresDownload={{
        text: translations.general_download_brochure,
        url: conversion.brochuresDownload.slug,
        preSelection: includedServiceSegments.map((s) =>
          prepareBrochureSelection(s),
        ),
      }}
      breadCrumbData={breadCrumbData}
      mobileBreadCrumbData={mobileBreadCrumbData}
      locale={locale}
    />
  );
};

export default ProductCollection;
